import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import { usePostUserQuestion } from '@oai-frontend-monorepo/api';

import jacob from '../../assets/images/jacob-options-ai.png';
import cc from '../../assets/images/cc-options-ai.png';
import john from '../../assets/images/john-options-ai.png';
import alex from '../../assets/images/alex-options-ai.png';
import matt from '../../assets/images/matt-options-ai.png';
import { PRIMARY } from '../../libs/mui';
import { Close } from '@mui/icons-material';

const QuestionModalContent = ({ onClose }: { onClose: () => void }) => {
  const [name, setName] = useState('');
  const [question, setQuestion] = useState('');
  const [touched, setTouched] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { mutate, isSuccess, error, isPending } = usePostUserQuestion();

  const handleSubmit = () => {
    setTouched(true);
    if (!name.trim() || !question.trim()) return;

    mutate(
      { name, question },
      {
        onSuccess: () => {
          setName('');
          setQuestion('');
          setTouched(false);
        },
      }
    );
  };

  if (isSuccess) {
    return (
      <Box
        sx={{
          width: 1050,
          height: isMobile ? '100vh' : 'auto',
          backgroundColor: '#fff',
          borderRadius: isMobile ? 0 : 1.5,
          boxShadow: 24,
          p: 6,
          py: 10,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {isMobile && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1,
              backgroundColor: '#fff',
              boxShadow: 2,
            }}
          >
            <Close />
          </IconButton>
        )}
        <Typography variant="h5" fontWeight="bold" mb={2}>
          ✅ Question submitted!
        </Typography>
        <Typography maxWidth={400} textAlign="center" variant="body1" mb={6}>
          Thanks for your question. Our team will get back to you within one
          business day.
        </Typography>
        <Button
          size="large"
          sx={{ width: 200 }}
          disableElevation
          variant="contained"
          onClick={onClose}
        >
          Okay
        </Button>
      </Box>
    );
  }

  const teamImages = [jacob, cc, john, alex, matt];

  return (
    <Box
      sx={{
        width: 1050,
        maxHeight: isMobile ? '100vh' : '90vh',
        overflowY: 'auto',
        backgroundColor: '#fff',
        borderRadius: isMobile ? 0 : 1.5,
        boxShadow: 24,
        p: isMobile ? 2 : 6,
        py: 10,
      }}
    >
      {isMobile && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1,
            backgroundColor: '#fff',
            boxShadow: 2,
          }}
        >
          <Close />
        </IconButton>
      )}
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <Box>
            <Box display="flex" gap={1} mb={2}>
              {[...Array(5)].map((_, idx) => {
                const img = teamImages[idx % teamImages.length];
                return (
                  <Box
                    key={idx}
                    sx={{
                      width: 65,
                      height: 65,
                      borderRadius: '50%',
                      bgcolor: '#ccc',
                      backgroundImage: `url(${img})`,

                      backgroundSize: 'cover',
                    }}
                  />
                );
              })}
            </Box>
            <Typography mt={3} variant="h6" fontWeight="670" fontSize={29}>
              Your team of options experts
            </Typography>
            <Typography variant="body2" mt={2}>
              Our mission goes beyond helping you find better trades with
              innovative visual tech. We’re here to add a human touch - sharing
              our decades of professional options trading experience to help you
              take your trading to the next level.
            </Typography>

            <Typography variant="body2" mt={2}>
              As an Options AI subscriber, you have access to our team of
              experts, on hand to answer all your trading questions! Ask us now
              and we’ll aim to get back to you within one trading day.
            </Typography>

            <Box
              mt={4}
              p={2}
              borderRadius={1.5}
              bgcolor={'rgba(231, 231, 231, 1)'}
            >
              <Typography
                fontWeight={400}
                color={'rgb(109, 109, 109)'}
                fontSize={10}
                lineHeight={1.8}
              >
                Please remember, we’re not investment advisors and we don’t make
                recommendations or give trade advice. We’ll do our best to
                answer your questions with usable insight and education to help
                you make your own trading decisions. We may publish any
                educational content we create in response to your question, but
                we will not share your name. See our full{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.optionsai.com/terms"
                  style={{
                    color: PRIMARY,
                    textDecoration: 'none',
                  }}
                >
                  Terms.
                </a>
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box bgcolor={PRIMARY} p={2} borderRadius={1.5}>
            <Typography
              fontSize={21}
              color="#fff"
              variant="h6"
              fontWeight={600}
              mb={1.5}
            >
              Ask us your trading question...
            </Typography>
            <Typography
              color="#fff"
              variant="body2"
              mb={3}
              fontWeight={300}
              fontSize={11.5}
            >
              For all account questions please email us at support@options.ai
            </Typography>

            <TextField
              // label="Your name"
              placeholder="Your name"
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 4 }}
              InputProps={{
                style: {
                  backgroundColor: '#fff',
                },
              }}
              error={touched && !name.trim()}
              helperText={touched && !name.trim() ? 'Name is required' : ''}
            />

            <TextField
              placeholder="Your question..."
              multiline
              rows={8}
              fullWidth
              size="small"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                style: {
                  backgroundColor: '#fff',
                },
              }}
              error={touched && !question.trim()}
              helperText={
                touched && !question.trim() ? 'Question is required' : ''
              }
            />

            {/* <Typography
              color="#fff"
              variant="body2"
              fontWeight={300}
              fontSize={11.5}
            >
              By submitting a question, you agree that we may publish or share
              any educational content that may be produced in response. We will
              not share or publish your name.
            </Typography> */}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {(error as unknown as Error).message || 'Something went wrong'}
              </Alert>
            )}
          </Box>

          <Button
            size="large"
            disableElevation
            sx={{ mt: 2 }}
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuestionModalContent;
